import { Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Section } from "../view/section";
import type { UnitFormRow } from "./types";

interface Props {
  units: UnitFormRow[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof UnitFormRow, value: unknown) => void;
}

export function SectionUnitsForm({ units, onAdd, onRemove, onUpdate }: Props): React.ReactElement {
  return (
    <Section id="units" title="Residential Unit Schedule">
      <p className="text-xs text-muted-foreground mb-3">
        ANZVGP 104 A1.2: state gross floor area for each residential unit. Natural Hazards Insurance Act 2023 cover is
        per dwelling.
      </p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-2 py-1.5" style={{ minWidth: 120 }}>
                Unit
              </th>
              <th className="text-left px-2 py-1.5" style={{ minWidth: 120 }}>
                Use
              </th>
              <th className="text-left px-2 py-1.5" style={{ minWidth: 140 }}>
                Configuration
              </th>
              <th className="text-right px-2 py-1.5">Gross Floor Area (m²)</th>
              <th className="text-center px-2 py-1.5">Included in Estimate</th>
              <th className="w-7" />
            </tr>
          </thead>
          <tbody>
            {units.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted-foreground py-8">
                  No units added.
                </td>
              </tr>
            ) : (
              units.map((row, i) => (
                <tr key={row.tempId} className="border-b hover:bg-accent/30">
                  <td className="p-0.5">
                    <input
                      type="text"
                      value={row.label}
                      onChange={(e) => onUpdate(i, "label", e.target.value)}
                      className="w-full h-7 rounded border border-input bg-white px-1.5 text-xs"
                      data-testid={`unit-label-${row.tempId}`}
                    />
                  </td>
                  <td className="p-0.5">
                    <input
                      type="text"
                      value={row.use}
                      onChange={(e) => onUpdate(i, "use", e.target.value)}
                      className="w-full h-7 rounded border border-input bg-white px-1.5 text-xs"
                      data-testid={`unit-use-${row.tempId}`}
                    />
                  </td>
                  <td className="p-0.5">
                    <input
                      type="text"
                      value={row.configuration}
                      onChange={(e) => onUpdate(i, "configuration", e.target.value)}
                      placeholder="e.g. 3-bedroom"
                      className="w-full h-7 rounded border border-input bg-white px-1.5 text-xs"
                      data-testid={`unit-configuration-${row.tempId}`}
                    />
                  </td>
                  <td className="p-0.5">
                    <input
                      type="number"
                      step="any"
                      value={row.grossFloorArea ?? ""}
                      onChange={(e) => onUpdate(i, "grossFloorArea", e.target.value ? Number(e.target.value) : null)}
                      className="w-24 h-7 rounded border border-input bg-white px-1.5 text-xs text-right tabular-nums"
                      data-testid={`unit-gross-floor-area-${row.tempId}`}
                    />
                  </td>
                  <td className="p-0.5 text-center">
                    <input
                      type="checkbox"
                      checked={row.includedInEstimate}
                      onChange={(e) => onUpdate(i, "includedInEstimate", e.target.checked)}
                      className="h-3.5 w-3.5"
                      data-testid={`unit-included-${row.tempId}`}
                    />
                  </td>
                  <td className="px-1">
                    <button
                      type="button"
                      onClick={() => onRemove(i)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`remove-unit-${row.tempId}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid="add-unit-row">
          Add Unit
        </Button>
      </div>
    </Section>
  );
}
